import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  increment,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { CheckInCreateSchema, type CheckInCreate } from '../models/checkIn.schemas';
import type { CheckIn } from '../models/checkIn.types';
import { objectiveService } from './objectiveService';

/**
 * Classe de serviço para operações Firestore relacionadas a Check-ins
 * Check-ins são IMUTÁVEIS - uma vez criados, não podem ser editados ou deletados
 */
class CheckInServiceClass {
  private collectionName = 'keyResults';
  private subCollectionName = 'checkIns';

  /**
   * Cria um novo check-in (IMUTÁVEL)
   * @param keyResultId - ID do Key Result
   * @param data - Dados do check-in a ser criado
   * @returns ID do check-in criado
   * @throws Error se a data do check-in for no futuro
   */
  async create(keyResultId: string, data: CheckInCreate): Promise<string> {
    // Validar com Zod schema
    const validated = CheckInCreateSchema.parse(data);

    // Explicitly omit confidence fields (defensive coding)
    const { confidenceScore, confidenceChange, ...cleanData } = validated as any;

    // Verificar se o Key Result existe
    const krDoc = await getDoc(doc(db, this.collectionName, keyResultId));
    if (!krDoc.exists()) {
      throw new Error('okr/key-result-not-found: Key Result não encontrado');
    }

    // Validar que a data não é no futuro
    const checkInDate = cleanData.checkInDate || new Date();
    if (checkInDate > new Date()) {
      throw new Error('okr/future-date-not-allowed: Data do check-in não pode ser no futuro');
    }

    // Criar documento na subcoleção
    const docRef = await addDoc(
      collection(db, this.collectionName, keyResultId, this.subCollectionName),
      {
        ...cleanData,
        checkInDate,
        createdAt: serverTimestamp(),
      }
    );

    // Atualizar o Key Result com o novo valor atual
    // Calcular progresso: ((currentValue - initialValue) / (targetValue - initialValue)) * 100
    // IMPORTANTE: Permite progresso > 100% quando meta é ultrapassada
    const krData = krDoc.data();
    const newValue = cleanData.newValue;
    const initialValue = krData.initialValue;
    const targetValue = krData.targetValue;

    let progress = 0;
    if (targetValue !== initialValue) {
      progress = Math.round(((newValue - initialValue) / (targetValue - initialValue)) * 100);
      progress = Math.max(0, progress); // Apenas previne progresso negativo
    }

    await updateDoc(doc(db, this.collectionName, keyResultId), {
      currentValue: newValue,
      progress: progress,
      lastCheckInDate: serverTimestamp(),
      checkInCount: increment(1),
      updatedAt: serverTimestamp(),
    });

    // Propagate progress to parent Objective
    const objectiveId = krData.objectiveId as string;
    await objectiveService.propagateProgressFromKeyResults(objectiveId);

    return docRef.id;
  }

  /**
   * Busca check-ins de um Key Result
   * @param keyResultId - ID do Key Result
   * @param limitCount - Número máximo de check-ins a retornar (opcional)
   * @returns Lista de check-ins ordenados por data (mais recentes primeiro)
   */
  async getByKeyResult(keyResultId: string, limitCount?: number): Promise<CheckIn[]> {
    // Verificar se o Key Result existe
    const krDoc = await getDoc(doc(db, this.collectionName, keyResultId));
    if (!krDoc.exists()) {
      throw new Error('okr/key-result-not-found: Key Result não encontrado');
    }

    // Construir query
    const constraints = [orderBy('checkInDate', 'desc')];
    if (limitCount) {
      constraints.push(limit(limitCount));
    }

    const checkInsQuery = query(
      collection(db, this.collectionName, keyResultId, this.subCollectionName),
      ...constraints
    );

    const snapshot = await getDocs(checkInsQuery);
    return snapshot.docs.map((doc) => this.formatCheckIn(doc.id, doc.data()));
  }

  /**
   * Busca todo o histórico de check-ins de um Key Result
   * @param keyResultId - ID do Key Result
   * @returns Lista completa de check-ins ordenados por data (mais recentes primeiro)
   */
  async getHistory(keyResultId: string): Promise<CheckIn[]> {
    return this.getByKeyResult(keyResultId);
  }

  /**
   * Inscreve-se para atualizações em tempo real de check-ins
   * @param keyResultId - ID do Key Result
   * @param callback - Função de callback chamada quando há atualizações
   * @returns Função de cancelamento de inscrição
   */
  subscribeToCheckIns(
    keyResultId: string,
    callback: (checkIns: CheckIn[]) => void
  ): () => void {
    const checkInsQuery = query(
      collection(db, this.collectionName, keyResultId, this.subCollectionName),
      orderBy('checkInDate', 'desc')
    );

    const unsubscribe = onSnapshot(
      checkInsQuery,
      (snapshot) => {
        const checkIns = snapshot.docs.map((doc) => this.formatCheckIn(doc.id, doc.data()));
        callback(checkIns);
      },
      (error) => {
        console.error('Error in checkIns subscription:', error);
        callback([]);
      }
    );

    return unsubscribe;
  }

  /**
   * Formata dados do Firestore para o tipo CheckIn
   * @private
   */
  private formatCheckIn(id: string, data: unknown): CheckIn {
    const checkIn = data as Record<string, unknown>;

    return {
      id,
      checkInDate: this.toTimestamp(checkIn.checkInDate),
      previousValue: checkIn.previousValue as number,
      newValue: checkIn.newValue as number,
      progressChange: checkIn.progressChange as number,
      impediments: (checkIn.impediments as string) || undefined,
      notes: (checkIn.notes as string) || undefined,
      actionsRequired: (checkIn.actionsRequired as string) || undefined,
      recordedById: checkIn.recordedById as string,
      recordedByName: checkIn.recordedByName as string,
      cycleEventId: (checkIn.cycleEventId as string) || undefined,
      createdAt: this.toTimestamp(checkIn.createdAt),
    } as CheckIn;
  }

  /**
   * Garante que o valor é um Timestamp do Firestore
   * @private
   */
  private toTimestamp(value: unknown): Timestamp {
    if (value instanceof Timestamp) {
      return value;
    }
    if (value instanceof Date) {
      return Timestamp.fromDate(value);
    }
    return Timestamp.now();
  }
}

export const checkInService = new CheckInServiceClass();
